// src/pages/support/SupportRequestCard.jsx
//
// Inline "Contact support" card for the Help & Support page. Replaces
// the old bare mailto: link with a structured request that writes to
// the existing `feedback` collection (type: 'support'), so it inherits:
//   - the hardened feedback security rules (userId == auth.uid, ≤5000 chars)
//   - the onFeedbackCreated_notifyFounder Cloud Function (founder email)
//   - the /admin FeedbackAdmin view
//
// Context is sourced ONLY from the current authenticated user (security
// review S-3): uid, email, displayName, role, current teamId, plan,
// platform, app version, page. No cross-user / roster / parent data.
//
// Firestore-first with a mailto fallback (same pattern as
// BetaFeedbackButton) so the channel never fully breaks — important on
// native Android where the OS mailto handler can be unreliable, which
// is exactly why Firestore is the primary path.

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { Capacitor } from '@capacitor/core'
import toast from 'react-hot-toast'
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/context/AuthContext'
import { useTeam } from '@/context/TeamContext'
import { BETA_FOUNDER_EMAIL } from '@/lib/betaConfig'

const HEAD_FONT = "'Barlow Condensed', sans-serif"
const MAX_LEN = 5000

const SUPPORT_CATEGORIES = ['billing', 'account', 'bug', 'feature', 'other']

export default function SupportRequestCard() {
  const { t } = useTranslation()
  const { firebaseUser, userProfile } = useAuth()
  const { currentTeam } = useTeam()

  const [category, setCategory] = useState('billing')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const email = firebaseUser?.email || userProfile?.email || null

  const reset = () => {
    setMessage('')
    setCategory('billing')
    setSent(false)
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    const trimmed = message.trim()
    if (!trimmed) {
      toast.error(t('help.support.emptyError'))
      return
    }
    setSubmitting(true)

    // Context — current user only (security S-3). Best-effort plan/version.
    const ctx = {
      type: 'support',
      category,
      source: 'help_page',
      message: trimmed.slice(0, MAX_LEN),
      userId: firebaseUser?.uid || null,
      userEmail: email,
      displayName: userProfile?.displayName || firebaseUser?.displayName || null,
      role: userProfile?.role || null,
      teamId: currentTeam?.id || null,
      plan: userProfile?.plan || userProfile?.tier || null,
      platform: Capacitor?.isNativePlatform?.() ? 'native' : 'web',
      appVersion: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_VERSION) || null,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
      createdAt: serverTimestamp(),
    }

    try {
      await addDoc(collection(db, 'feedback'), ctx)
      setSent(true)
    } catch (err) {
      // PII-safe log (security S-4): codes only, never the message/email.
      console.warn('[SupportRequestCard] write failed:', err?.code, err?.message)
      // mailto fallback so the channel never fully breaks.
      const subject = encodeURIComponent(`[Scrimly support · ${category}] ${trimmed.slice(0, 50)}`)
      const body = encodeURIComponent(
        [trimmed, '', '---', `From: ${email || '(unknown)'}`, `Page: ${ctx.pageUrl}`].join('\n')
      )
      toast.error(t('help.support.errorToast'))
      window.location.href = `mailto:${BETA_FOUNDER_EMAIL}?subject=${subject}&body=${body}`
    } finally {
      setSubmitting(false)
    }
  }

  // ── Success state ────────────────────────────────────────────────
  if (sent) {
    return (
      <div className="rounded-2xl border border-[#b5f03d]/40 bg-[#b5f03d]/[0.08] p-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#b5f03d]/20">
          <CheckCircleIcon className="h-7 w-7 text-[#5a8a1f]" />
        </div>
        <p
          className="uppercase text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20, letterSpacing: '0.4px' }}
        >
          {t('help.support.successTitle')}
        </p>
        <p className="mt-1 text-sm text-zinc-600">
          {t('help.support.successBody', { email: email || '' })}
        </p>
        <button
          type="button"
          onClick={reset}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          {t('help.support.sendAnother')}
        </button>
      </div>
    )
  }

  // ── Form ─────────────────────────────────────────────────────────
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#b5f03d]/15">
          <EnvelopeIcon className="h-5 w-5 text-[#5a8a1f]" />
        </div>
        <div>
          <h3
            className="uppercase text-[#0d1f14] leading-none"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 18, letterSpacing: '0.4px' }}
          >
            {t('help.support.cardTitle')}
          </h3>
          <p className="text-xs text-zinc-500 mt-1">{t('help.support.replyTime')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Category */}
        <div>
          <label
            className="block text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1.5"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('help.support.categoryLabel')}
          </label>
          <div className="flex flex-wrap gap-1.5">
            {SUPPORT_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={[
                  'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors border',
                  category === c
                    ? 'bg-[#b5f03d]/15 border-[#b5f03d] text-[#0d1f14]'
                    : 'bg-white border-zinc-200 text-zinc-600 hover:border-[#b5f03d]/40',
                ].join(' ')}
              >
                {t(`help.support.categories.${c}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label
            htmlFor="support-message"
            className="block text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1.5"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('help.support.messageLabel')}
          </label>
          <textarea
            id="support-message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
            placeholder={t('help.support.messagePlaceholder')}
            rows={5}
            maxLength={MAX_LEN}
            className="w-full resize-none rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
          />
          <p className="mt-1.5 text-[11px] text-zinc-500">{t('help.support.autoIncluded')}</p>
        </div>

        <button
          type="submit"
          disabled={submitting || !message.trim()}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[48px] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 15, letterSpacing: '0.6px' }}
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          {submitting ? t('help.support.sending') : t('help.support.send')}
        </button>
      </form>
    </div>
  )
}
